/**
 * El único sitio del front que habla con la red.
 *
 * Tres reglas que vienen de SPEC-009 y que no se relajan aquí:
 *
 *  1. **Ni un token en JavaScript.** La sesión viaja en cookies `httpOnly`, así
 *     que este módulo nunca lee, guarda ni ve un token. `localStorage` está
 *     prohibido para esto: ahí lo lee cualquier XSS y no caduca al cerrar la
 *     pestaña.
 *  2. **`X-Recauda-Cliente: navegador` en todo.** Es lo que elige la entrega por
 *     cookie y, de paso, la defensa contra CSRF: un formulario malicioso no
 *     puede poner una cabecera personalizada sin permiso previo.
 *  3. **`credentials: "include"`.** Sin esto, en producción —donde el front vive
 *     en otro origen— el navegador no adjuntaría la cookie.
 */

import type { EligeCuenta } from "./tipos.ts";

/** Vacío en desarrollo: el proxy de Vite pone la API en el mismo origen. */
/** Se exporta para los enlaces de descarga, que no pasan por `pedir`. */
export const BASE = (import.meta.env.VITE_API ?? "").replace(/\/$/, "");

export class ErrorApi extends Error {
  estado: number;
  datos: Record<string, unknown>;

  constructor(estado: number, mensaje: string, datos: Record<string, unknown> = {}) {
    super(mensaje);
    this.name = "ErrorApi";
    this.estado = estado;
    this.datos = datos;
  }

  /** 401 sin sesión recuperable: hay que volver a entrar. */
  get esSinSesion() {
    return this.estado === 401;
  }

  /** 403: la sesión es válida, el rol no alcanza. */
  get esSinPermiso() {
    return this.estado === 403;
  }

  /** 409: el motor no pudo leer los datos de entrada. No es culpa de la sesión. */
  get esDatosRotos() {
    return this.estado === 409;
  }
}

/** Se dispara cuando el refresco falla: la app entera vuelve al login. */
type Oyente = () => void;
const oyentesSinSesion = new Set<Oyente>();

export function alPerderSesion(oyente: Oyente): () => void {
  oyentesSinSesion.add(oyente);
  return () => {
    oyentesSinSesion.delete(oyente);
  };
}

interface Opciones {
  metodo?: "GET" | "POST" | "PUT" | "DELETE";
  cuerpo?: unknown;
  /** Rutas públicas (login, aceptar invitación): un 401 aquí no significa "refresca". */
  publica?: boolean;
  senal?: AbortSignal;
}

async function crudo(ruta: string, opciones: Opciones): Promise<Response> {
  const { metodo = "GET", cuerpo, senal } = opciones;
  return fetch(`${BASE}${ruta}`, {
    method: metodo,
    // Sin esto la cookie no cruza a otro origen, y en producción sí lo es.
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-recauda-cliente": "navegador",
    },
    body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo),
    signal: senal,
  });
}

/**
 * El acceso vive diez minutos. Que la sesión se caiga a media tarde por eso
 * sería un defecto de este archivo, no del backend: ante un 401 se refresca una
 * vez y se reintenta.
 *
 * `refrescoEnCurso` evita la estampida: si cinco peticiones fallan a la vez, se
 * refresca una sola vez y las cinco esperan el mismo resultado.
 */
let refrescoEnCurso: Promise<boolean> | null = null;

function refrescar(): Promise<boolean> {
  refrescoEnCurso ??= crudo("/api/v1/sesion/refrescar", { metodo: "POST", cuerpo: {} })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      // En el microtask siguiente: quien ya estaba esperando recibe este mismo
      // resultado, y la próxima tanda vuelve a intentarlo de cero.
      queueMicrotask(() => (refrescoEnCurso = null));
    });
  return refrescoEnCurso;
}

async function leer(r: Response): Promise<Record<string, unknown>> {
  return (await r.json().catch(() => ({}))) as Record<string, unknown>;
}

export async function pedir<T>(ruta: string, opciones: Opciones = {}): Promise<T> {
  let respuesta = await crudo(ruta, opciones);

  if (respuesta.status === 401 && !opciones.publica) {
    if (await refrescar()) {
      respuesta = await crudo(ruta, opciones);
    } else {
      oyentesSinSesion.forEach((f) => f());
      throw new ErrorApi(401, "Tu sesión expiró. Vuelve a entrar.");
    }
  }

  const datos = await leer(respuesta);

  if (!respuesta.ok) {
    if (respuesta.status === 401 && !opciones.publica) oyentesSinSesion.forEach((f) => f());
    const mensaje =
      typeof datos.error === "string" ? datos.error : `No se pudo completar (${respuesta.status}).`;
    throw new ErrorApi(respuesta.status, mensaje, datos);
  }

  return datos as T;
}

/**
 * Login. Se separa de `pedir` porque su 300 no es un error: es el backend
 * diciendo "tienes varias cuentas, elige una".
 */
export async function abrirSesion(entrada: {
  correo: string;
  contrasena: string;
  slug?: string;
}): Promise<{ tipo: "dentro"; datos: Record<string, unknown> } | { tipo: "elige"; datos: EligeCuenta }> {
  const respuesta = await crudo("/api/v1/sesion", { metodo: "POST", cuerpo: entrada });
  const datos = await leer(respuesta);

  if (respuesta.status === 300) return { tipo: "elige", datos: datos as unknown as EligeCuenta };
  if (!respuesta.ok) {
    throw new ErrorApi(
      respuesta.status,
      typeof datos.error === "string" ? datos.error : "No se pudo entrar.",
      datos,
    );
  }
  return { tipo: "dentro", datos };
}
